import { useCallback, useRef, useState } from 'react'

import {
  ButtonProps,
  Input,
  InputProps,
  Text,
} from '@hhgtech/hhg-components/mantine'
import { Link } from '@hhgtech/icons/core'
import { LinkThin } from '@hhgtech/icons/other'
import {
  Box,
  BoxProps,
  Popover,
  Button,
  CopyButton,
  PopoverProps,
} from '@mantine/core'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useRouterExtend } from 'hooks/useRouterExtend'

import useStyles from './index.styles'

export type CopyInputProps = {
  shareUrl?: string
  btnProps?: ButtonProps
  inputProps?: InputProps
  type?: 'input' | 'button'
  isHeader?: boolean
  isRevamp?: boolean
  popoverProps?: Partial<PopoverProps>
} & BoxProps
export const CopyInput = ({
  btnProps,
  inputProps,
  styles,
  classNames,
  className,
  type = 'input',
  shareUrl: shareUrlProp,
  isHeader,
  popoverProps,
  isRevamp,
  ...rest
}: CopyInputProps) => {
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const currentUrl = useCurrentUrl()
  const shareUrlLink = shareUrlProp || currentUrl
  const shareUrl = shareUrlLink.startsWith('http')
    ? shareUrlLink
    : `${getHelloSitesUrl(locale)}${shareUrlLink}`
  const { classes, cx } = useStyles(
    { type },
    {
      name: 'ShareBox__CopyInput',
      styles,
      classNames,
    },
  )

  const inputRef = useRef<HTMLInputElement>(null)
  const [isCopied, setIsCopied] = useState(false)
  const [opened, setOpened] = useState(false)
  const withPopover = type === 'button'

  const handleClickCopy = useCallback(
    (copy: () => void) => () => {
      copy()
      setIsCopied(true)
      withPopover && setOpened(true)
      setTimeout(() => {
        setIsCopied(false)
        withPopover && setOpened(false)
      }, 3000)
    },
    [withPopover],
  )

  if (isRevamp) {
    return (
      <CopyButton value={shareUrl || ''}>
        {({ copy }) => (
          <Popover
            radius={'sm'}
            withArrow
            shadow="sm"
            opened={opened}
            onChange={setOpened}
            classNames={{
              dropdown: classes.popoverDropdown,
              arrow: classes.popoverArrow,
            }}
            {...popoverProps}
          >
            <Popover.Target>
              <Box
                className="w-10 h-10 p-2 relative cursor-pointer rounded-lg hover:bg-gray100"
                data-event-category="Social Share"
                data-event-action="Copy Link"
                data-event-label={currentUrl}
                onClick={handleClickCopy(copy)}
              >
                <LinkThin />
              </Box>
            </Popover.Target>
            <Popover.Dropdown>
              <Text size="n1" color="#fff">
                {f({
                  id: 'authorPage.share.copiedButton',
                })}
              </Text>
            </Popover.Dropdown>
          </Popover>
        )}
      </CopyButton>
    )
  }

  return (
    <Box className={cx(className, classes.root)} {...rest}>
      <Text
        size="p4"
        style={{ marginBottom: '0.5em' }}
        className={classes.heading}
      >
        {f({
          id: 'authorPage.share.orCopyLink',
        })}
      </Text>
      <Input
        ref={inputRef}
        value={shareUrl}
        name="sharedUrlInput"
        size="md"
        classNames={{
          root: cx('input-share', classes.inputWrapper),
          input: classes.input,
          rightSection: classes.inputRightSection,
        }}
        rightSection={
          <CopyButton value={shareUrl || ''}>
            {({ copied, copy }) => (
              <Popover
                radius={'sm'}
                withArrow
                shadow="sm"
                opened={opened}
                onChange={setOpened}
                classNames={{
                  dropdown: classes.popoverDropdown,
                  arrow: classes.popoverArrow,
                }}
                {...popoverProps}
              >
                <Popover.Target>
                  <Button
                    classNames={{
                      root: cx(
                        'input-share-btn pointer-event-child-none',
                        classes.btn,
                        isCopied ? 'copied' : '',
                      ),
                      label: classes.btnLabel,
                      leftIcon: classes.btnLeftIcon,
                    }}
                    leftIcon={
                      type === 'button' ? <Link size={18} /> : undefined
                    }
                    variant="ghost"
                    size="md"
                    data-event-category="Social Share"
                    data-event-action={
                      isHeader ? `Header - Copy Link ` : 'Copy Link'
                    }
                    data-event-label={currentUrl}
                    onClick={handleClickCopy(copy)}
                  >
                    {type === 'input' ? (
                      <Text size="s4" color="blue">
                        {btnProps?.children ||
                          (copied
                            ? f({
                                id: 'authorPage.share.copiedButton',
                              })
                            : f({
                                id: 'authorPage.share.copyButton',
                              }))}
                      </Text>
                    ) : undefined}
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Text size="n1" color="#fff">
                    {f({
                      id: 'authorPage.share.copiedButton',
                    })}
                  </Text>
                </Popover.Dropdown>
              </Popover>
            )}
          </CopyButton>
        }
        readOnly
      />
    </Box>
  )
}
